import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Loader2, CalendarDays, Plug, RefreshCw, CheckCircle2, AlertCircle, Link2,
  Users, Wrench, Activity, Clock, XCircle, CalendarClock, RotateCcw, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATE_DOT = {
  connected: "bg-emerald-500", connecting: "bg-amber-400", error: "bg-red-500", not_connected: "bg-slate-300",
};
const STATE_LABEL = {
  connected: "Connected", connecting: "Connecting", error: "Connection error", not_connected: "Not connected",
};

export default function BookingSystems() {
  const [providers, setProviders] = useState(null);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => Promise.all([
    api.get("/booking/providers").then((r) => setProviders(r.data.providers)),
    api.get("/booking/dashboard").then((r) => setDash(r.data)),
  ]), []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (params.get("connected")) { toast.success(`${params.get("connected")} connected & synced`); setParams({}, { replace: true }); load(); }
    else if (params.get("error")) { toast.error("Could not connect — please try again."); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const connect = async (p) => {
    setBusy(p.provider);
    try { const r = await api.get(`/booking/connect/${p.provider}`); window.location.href = r.data.authorize_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot connect yet"); setBusy(""); }
  };
  const disconnect = async (p) => { setBusy(p.provider); try { await api.post("/booking/disconnect", { provider: p.provider }); toast.success("Disconnected"); load(); } catch { toast.error("Failed"); } finally { setBusy(""); } };
  const test = async (p) => { setBusy(p.provider); try { await api.post("/booking/test", { provider: p.provider }); toast.success("Connection healthy"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Connection error"); load(); } finally { setBusy(""); } };
  const sync = async (p) => { setBusy(p.provider || "sync"); try { const r = await api.post("/booking/sync", p ? { provider: p.provider } : {}); toast.success(`Synced — ${r.data.services || 0} services, ${r.data.staff || 0} staff`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); } finally { setBusy(""); } };

  if (!providers) return <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading booking systems…</div>;
  const anyConnected = providers.some((p) => p.connected);

  return (
    <div data-testid="booking-systems-view">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Booking Systems</h1>
        <p className="text-slate-500 mt-1">Connect your existing booking platform so your AI receptionist checks live availability, books, reschedules and cancels — never inventing times.</p>
      </div>

      {/* Dashboard */}
      {anyConnected && dash && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="booking-dashboard">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span className={`h-2.5 w-2.5 rounded-full ${STATE_DOT[dash.health] || "bg-slate-300"}`} />
              <span className="font-semibold text-slate-900">{dash.provider_name}</span>
              <span className="text-xs text-slate-400">· {STATE_LABEL[dash.health] || dash.health}</span>
            </div>
            <div className="flex gap-2">
              <Button data-testid="manual-sync-btn" size="sm" variant="outline" onClick={() => sync({ provider: dash.connected_provider })} disabled={busy} className="gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Sync now</Button>
              <Button data-testid="reconnect-btn" size="sm" variant="outline" onClick={() => connect({ provider: dash.connected_provider })} className="gap-1.5"><RotateCcw className="h-3.5 w-3.5" /> Reconnect</Button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[["Appointments today", dash.appointments_today, CalendarClock, "text-blue-500"],
              ["Booked by AI", dash.ai_bookings, Activity, "text-emerald-500"],
              ["Cancelled", dash.cancelled_bookings, XCircle, "text-red-500"],
              ["Rescheduled", dash.rescheduled_bookings, RotateCcw, "text-amber-500"],
              ["Failed syncs", dash.failed_syncs, AlertCircle, "text-red-500"],
              ["Last sync", dash.last_sync ? new Date(dash.last_sync).toLocaleTimeString() : "—", Clock, "text-slate-400"]].map(([l, v, Icon, tone]) => (
              <div key={l} className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><Icon className={`h-3 w-3 ${tone}`} /> {l}</div>
                <p className="mt-1 font-bold text-slate-900 text-lg">{v}</p>
              </div>
            ))}
          </div>
          {dash.scheduling_url && <p className="mt-3 text-xs text-slate-500 flex items-center gap-1"><Link2 className="h-3 w-3" /> Booking link the AI shares: <a href={dash.scheduling_url} target="_blank" rel="noreferrer" className="text-blue-600">{dash.scheduling_url}</a></p>}
          {(dash.services?.length > 0 || dash.staff?.length > 0) && (
            <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
              {dash.services?.length > 0 && <span className="flex items-center gap-1"><Wrench className="h-3 w-3" /> {dash.services.length} services imported</span>}
              {dash.staff?.length > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {dash.staff.length} staff imported</span>}
            </div>
          )}
        </div>
      )}

      {/* Provider cards */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {providers.map((p) => {
          const comingSoon = !p.available && p.kind === "salon";
          const setupPending = !p.available && p.kind !== "salon";
          return (
            <div key={p.provider} data-testid={`booking-provider-${p.provider}`} className={`rounded-xl border bg-white p-5 flex flex-col ${p.connected ? "border-emerald-200 ring-1 ring-emerald-100" : "border-slate-200"}`}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><CalendarDays className="h-5 w-5" /></div>
                <div className="flex items-center gap-1.5" data-testid={`booking-status-${p.provider}`}>
                  <span className={`h-2 w-2 rounded-full ${STATE_DOT[p.status] || "bg-slate-300"}`} />
                  <span className="text-[11px] font-semibold text-slate-500">{comingSoon ? "Coming soon" : setupPending ? "Setup pending" : STATE_LABEL[p.status]}</span>
                </div>
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{p.name}</h3>
              <p className="text-xs text-slate-400 mt-1 leading-snug min-h-[32px]">{p.blurb}</p>
              {p.connected && (
                <div className="mt-2 text-[11px] text-slate-500 space-y-0.5">
                  {p.account_name && <p className="truncate">Account: <b className="text-slate-700">{p.account_name}</b></p>}
                  {p.last_sync && <p>Last sync: {new Date(p.last_sync).toLocaleString()}</p>}
                  {(p.imported?.services > 0 || p.imported?.staff > 0) && <p>{p.imported.services} services · {p.imported.staff} staff</p>}
                </div>
              )}
              <div className="mt-auto pt-4 space-y-1.5">
                {p.connected ? (
                  <>
                    <div className="flex gap-1.5">
                      <Button data-testid={`booking-manage-${p.provider}`} variant="outline" onClick={() => sync(p)} disabled={busy === p.provider} className="flex-1 h-8 text-xs gap-1"><RefreshCw className="h-3 w-3" /> Sync</Button>
                      <Button data-testid={`booking-test-${p.provider}`} variant="outline" onClick={() => test(p)} disabled={busy === p.provider} className="flex-1 h-8 text-xs">Test</Button>
                    </div>
                    <Button data-testid={`booking-disconnect-${p.provider}`} variant="outline" onClick={() => disconnect(p)} disabled={busy === p.provider} className="w-full h-8 text-xs text-red-600 hover:text-red-700">Disconnect</Button>
                  </>
                ) : comingSoon ? (
                  <Button disabled variant="outline" className="w-full h-8 text-xs text-slate-400">Coming soon</Button>
                ) : setupPending ? (
                  <Button disabled variant="outline" className="w-full h-8 text-xs text-slate-400">Awaiting credentials</Button>
                ) : (
                  <Button data-testid={`booking-connect-${p.provider}`} onClick={() => connect(p)} disabled={busy === p.provider} className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 gap-1">{busy === p.provider ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plug className="h-3 w-3" /> Connect</>}</Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
